import React, { useEffect, useMemo, useState } from "react";
import {
  Stack,
  TextField,
  Select,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Divider,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import {
  CARE_GAP_STATUS_OPTIONS,
  buildGroupedWorkflowItems,
} from "../../common/constants";
import { getLocations, getPcps } from "../../../../utils/services/control/masterDataService";

export default function WorklistFilters({ value, workflowStatuses = [], onChange, onApply, onClear }) {
  const [locations, setLocations] = useState([]);
  const [pcps, setPcps] = useState([]);

  // Catálogo de workflow statuses agrupado (endpoint GET /api/CareGapsV2/workflow-statuses).
  const workflowItems = useMemo(
    () => buildGroupedWorkflowItems(workflowStatuses),
    [workflowStatuses]
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getLocations({ status: 1, clinical: true }).catch(() => []),
      getPcps({ status: 1 }).catch(() => []),
    ]).then(([locs, pcpList]) => {
      if (cancelled) return;
      setLocations(
        (Array.isArray(locs) ? locs : [])
          .map((l) => ({ id: l.locationId, name: l.medicalCenter || l.centerKey || `Location ${l.locationId}` }))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      );
      setPcps(
        (Array.isArray(pcpList) ? pcpList : [])
          .map((p) => ({ id: p.employeePcpId, label: p.title ? `${p.pcpFullName} (${p.title})` : p.pcpFullName }))
          .filter((p) => !!p.id)
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    });
    return () => { cancelled = true; };
  }, []);

  const set = (key) => (eOrVal) => {
    const val = eOrVal?.target ? eOrVal.target.value : eOrVal;
    onChange({ ...value, [key]: val === "" ? null : val });
  };

  const dobValue = value.dateOfBirth ? dayjs(value.dateOfBirth) : null;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" fontWeight={700}>Filters</Typography>

      <FormControl size="small" fullWidth>
        <InputLabel>Care Gap Status</InputLabel>
        <Select
          label="Care Gap Status"
          value={value.careGapStatusId ?? ""}
          onChange={(e) => onChange({ ...value, careGapStatusId: e.target.value === "" ? null : Number(e.target.value) })}
        >
          <MenuItem value=""><em>All</em></MenuItem>
          {CARE_GAP_STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel>Workflow</InputLabel>
        <Select
          label="Workflow"
          value={value.workflowStatusId ?? ""}
          onChange={(e) => onChange({ ...value, workflowStatusId: e.target.value === "" ? null : Number(e.target.value) })}
        >
          <MenuItem value=""><em>All</em></MenuItem>
          {workflowItems.map((entry, idx) =>
            entry.type === "header" ? (
              <ListSubheader key={`header-${idx}`}>{entry.label}</ListSubheader>
            ) : (
              <MenuItem key={entry.value} value={entry.value}>
                {entry.label}
              </MenuItem>
            )
          )}
        </Select>
      </FormControl>

      <TextField
        size="small"
        label="HEDIS Measure"
        placeholder="e.g. Breast Cancer Screening"
        value={value.careGapName ?? ""}
        onChange={set("careGapName")}
        fullWidth
      />

      <Autocomplete
        size="small"
        options={pcps}
        value={pcps.find((p) => p.id === value.pcpEmployeeId) || null}
        onChange={(_, opt) => onChange({ ...value, pcpEmployeeId: opt ? opt.id : null })}
        getOptionLabel={(o) => (typeof o === "string" ? o : o?.label || "")}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        slotProps={{
          popper: { style: { width: "fit-content", minWidth: 320 } },
          paper: { sx: { minWidth: 320 } },
        }}
        renderInput={(params) => <TextField {...params} label="PCP" placeholder="All" />}
      />

      <FormControl size="small" fullWidth>
        <InputLabel>Location</InputLabel>
        <Select
          label="Location"
          value={value.locationId ?? ""}
          onChange={(e) => onChange({ ...value, locationId: e.target.value === "" ? null : Number(e.target.value) })}
        >
          <MenuItem value=""><em>All</em></MenuItem>
          {locations.map((l) => (
            <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        label="Search patient"
        placeholder="Last or first name"
        value={value.patientName ?? ""}
        onChange={set("patientName")}
        fullWidth
      />

      <TextField
        size="small"
        label="CIP ID"
        value={value.cipId ?? ""}
        onChange={set("cipId")}
        fullWidth
      />

      <DatePicker
        label="Date of birth"
        value={dobValue}
        onChange={(d) =>
          onChange({ ...value, dateOfBirth: d?.isValid?.() ? d.format("YYYY-MM-DD") : null })
        }
        format="YYYY-MM-DD"
        slotProps={{
          textField: { size: "small", fullWidth: true },
          field: { clearable: true, onClear: () => onChange({ ...value, dateOfBirth: null }) },
        }}
      />

      <Divider />

      <Stack direction="row" spacing={1}>
        <Button fullWidth variant="contained" onClick={onApply}>
          Apply
        </Button>
        <Button fullWidth variant="outlined" color="inherit" onClick={onClear}>
          Clear
        </Button>
      </Stack>
    </Stack>
  );
}
